import { BadgeCheck } from "lucide-react";

import { ComicPanel } from "@/components/comic";

import { GuestSharing } from "./guestApi";

/**
 * What a visitor is about to hand over, shown once, before anything has gone anywhere (D-92).
 *
 * <h2>Why it is here and not at the moment it matters</h2>
 *
 * <p>The obvious place to ask is when a request is about to reach a host. That is the one place it
 * cannot be asked. A question that only appears once somebody has been admitted answers itself:
 * being asked would tell the visitor they got through, which is exactly the oracle D-44 refuses.
 * D-80 met this already with the clarification questions and settled it as a rule about
 * <em>when</em> rather than about wording, because a rule about wording is one somebody breaks
 * later in good faith. So: asked once, up front, of everybody, before anything is sent.</p>
 *
 * <h2>The words</h2>
 *
 * <p>Flat and factual. It says who sees it and what they see, and stops. Not a warning, not a
 * reassurance, and no mention of privacy as a virtue — the visitor is being asked to decide, and
 * everything beyond the facts is pressure in one direction or the other.</p>
 *
 * <p>Declining is a real option with a real consequence, said plainly, and is not a dead end: they
 * keep the page. What they cannot do is send a request, because there would be nobody to reply to.</p>
 */
export function GuestSharingCard({
  sharing,
  onShare,
  onDecline,
  busy = false,
}: {
  sharing: GuestSharing;
  onShare: () => void;
  onDecline: () => void;
  busy?: boolean;
}) {
  const proposed = sharing.proposed;

  // Nothing to agree to. An identity with no address cannot be handed over, and inventing a card
  // that asks about nothing would be worse than saying so.
  if (!proposed) {
    return (
      <ComicPanel title="Nothing to share">
        <p className="text-sm text-muted-foreground">
          The identity you proved does not carry an email address, so there is no way for anyone to
          reply to you.
        </p>
      </ComicPanel>
    );
  }

  return (
    <ComicPanel title="Before you ask anyone">
      <div className="rounded-xl border border-border bg-card px-3 py-2.5">
        {proposed.name && <p className="text-sm font-medium">{proposed.name}</p>}
        <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
          {proposed.email_verified ? (
            <>
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-700" aria-hidden />
              <span className="truncate">{proposed.email}</span>
            </>
          ) : (
            <span className="truncate">{proposed.email}</span>
          )}
        </p>
      </div>

      <p className="mt-3 text-sm">Anyone you ask to meet will see your name and email address.</p>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onShare}
          disabled={busy}
          className="rounded-xl bg-brand-blue px-3 py-2 text-sm font-medium text-white transition hover:-translate-y-px disabled:opacity-60"
        >
          That's fine
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={busy}
          className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition hover:-translate-y-px disabled:opacity-60"
        >
          Don't share it
        </button>
      </div>
    </ComicPanel>
  );
}

/**
 * What to say when they try to send something having declined.
 *
 * <p>Explains the mechanism rather than scolding, and says nothing whatsoever about whether anyone
 * would have said yes — it is the same sentence whoever they asked and however it would have gone.</p>
 */
export const SHARING_REQUIRED =
  "Sharing your name and email is how someone can reply. Nothing goes out without it.";

/** What they are told on declining. Not a dead end: they keep the page. */
export const SHARING_DECLINED = "Nothing is shared and nothing is kept. You can still look around.";
